import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Replay from '@mui/icons-material/Replay';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useFlag } from '@reflag/react-sdk';
import {
  createLpCapitalActionEditRoute,
  createLpCapitalActionNoticeCreateRoute,
} from 'adminConstants/routes';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { useLayoutContext } from 'contexts/LayoutContext';
import { differenceInDays } from 'date-fns';
import { ActionStatus } from 'further-types/lp/action';
import { CapitalActionNoticeStatus } from 'further-types/lp/capital-action-notice';
import { CalendarDayRange } from 'further-types/utils';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { Column } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import { ActionStatusLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getQueryKey } from '@trpc/react-query';
import CapitalActionExpandedContent from './CapitalActionExpandedContent';
import DownloadCapitalNotices from './DownloadCapitalNotices';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

const NET_DISTRIBUTION_COLOR = '#C0C0C0';
const NET_CALL_COLOR = '#CFB53B';

type CapitalAction = {
  id: string;
  name: string;
  firmName: string;
  perInvestorBreakdown: Array<{
    id: string;
    fullName: string;
    totalCommitmentLcy: number;
    capitalDistributedLcy: number;
    capitalCalledLcy: number;
    amountPaidLcy: number;
    advanceSubscriptionUsedLcy: number;
  }>;
  isNetDistribution: boolean;
  status: ActionStatus;
  actionDueDate: CalendarDay;
  isPaid: boolean;
  investorsPaid: number;
  totalInvestors: number;
  amountPaid: number;
  capitalCallTotal: number;
  distributionTotal: number;
  netCallDistribution: number;
  totalAmount: number;
  canBeUpdated: boolean;
  reasonCannotBeUpdated: string;
  canBeDeleted: boolean;
  reasonCannotBeDeleted: string;
  capitalNotice: {
    id: string;
    status: CapitalActionNoticeStatus;
    noticeDate?: CalendarDay;
    investorPdfUploads: Array<{
      investorId: string;
      path: string;
      previewUrl: string;
    }>;
  } | null;
};

type Props = {
  capitalActions: Array<CapitalAction>;
};

const DateRangeFilter = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '275px',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

const StyledConfirmDialog = styled(ConfirmDialog)({
  '& .MuiDialogContent-root': {
    paddingBottom: '0px !important',
  },
});

const orderByOptions = ['actionDueDate'] as const;

const CapitalActions: FC<Props> = ({ capitalActions }) => {
  const history = useHistory();
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const vehicleId = selectedVehicleId ?? '';
  const baseCurrency = vehicle?.baseCurrency;
  const { toggleRowExpansion, expandedRows } = useExpandedRows();
  const recallCapitalNoticesDisclosure = useDisclosure<{ id: string }>();
  const deleteCapitalActionDisclosure = useDisclosure<{
    id: string;
    status: ActionStatus;
  }>();
  const [capitalAction, setCapitalAction] = useState<CapitalAction | null>(
    null,
  );
  const [notifyInvestors, setNotifyInvestors] = useState(true);
  const { isEnabled: showBankRecDataInCapitalNotices } = useFlag(
    'show-bank-rec-data-in-capital-notices',
  );

  const { filters, handleChangeFilters, pagination } = usePaginatedFilters<
    {
      investorName: string;
      actionDueDateRange?: CalendarDayRange;
    },
    (typeof orderByOptions)[number]
  >(`capital-actions-${vehicleId}`, {
    defaultFilters: {
      investorName: '',
    },
    defaultOrder: {
      orderBy: 'actionDueDate',
      order: 'desc',
    },
    orderByOptions,
  });

  const filteredCapitalActions = capitalActions
    .filter((capitalAction) =>
      filters.investorName && capitalAction.perInvestorBreakdown.length
        ? capitalAction.perInvestorBreakdown
            .map((investor) => investor.fullName)
            .some((investorName) =>
              investorName
                .toLowerCase()
                .includes(filters.investorName.toLowerCase()),
            )
        : true,
    )
    .filter((capitalAction) =>
      filters.actionDueDateRange
        ? capitalAction.actionDueDate.isWithinRange(filters.actionDueDateRange)
        : true,
    );

  const recallCapitalActionNotice = api.capitalActionNotice.recall.useMutation(
    useHandleTrpcMutation()({
      onSuccess: recallCapitalNoticesDisclosure.onClose,
    }),
  );

  const handleRecallCapitalActionNotice = () => {
    recallCapitalActionNotice.mutate({
      vehicleId,
      capitalActionNoticeId: recallCapitalNoticesDisclosure.actionProps.id,
    });
  };

  const deleteCapitalAction = api.capitalAction.delete.useMutation(
    useHandleTrpcMutation()({
      onSuccess: deleteCapitalActionDisclosure.onClose,
      evictions: [
        getQueryKey(api.capitalAction.get),
        getQueryKey(api.capitalAction.getTransactionTypes),
      ],
    }),
  );

  const handleDeleteCapitalAction = () => {
    deleteCapitalAction.mutate({
      vehicleId,
      capitalActionId: deleteCapitalActionDisclosure.actionProps.id,
      notifyInvestors:
        deleteCapitalActionDisclosure.actionProps.status ===
        ActionStatus.Approved
          ? notifyInvestors
          : false,
    });
  };

  const columns: Array<TableColumn<(typeof filteredCapitalActions)[number]>> = [
    {
      label: 'Action name',
      key: 'name',
      width: 310,
      render: ({ name, isNetDistribution }) => (
        <ColorIndicatorWithText
          text={name}
          color={isNetDistribution ? NET_DISTRIBUTION_COLOR : NET_CALL_COLOR}
        />
      ),
    },
    {
      label: 'Status',
      key: 'status',
      width: 160,
      render: ({ status, capitalNotice }) => {
        if (status === ActionStatus.Approved) {
          if (
            !capitalNotice ||
            capitalNotice.status === CapitalActionNoticeStatus.Draft
          ) {
            return 'Pending notice';
          } else if (
            capitalNotice.status === CapitalActionNoticeStatus.Published
          ) {
            return 'Notice sent';
          }
        }

        return ActionStatusLabels[status];
      },
    },
    {
      label: 'Due date',
      width: 120,
      key: 'actionDueDate',
      render: ({ status, actionDueDate, isPaid }) => {
        const today = new CalendarDay(new Date());
        const daysUntilDue = differenceInDays(
          actionDueDate.toUTCDate(),
          today.toUTCDate(),
        );
        const daysUntilDueColor =
          status === ActionStatus.Approved
            ? !isPaid && daysUntilDue <= 0
              ? 'error'
              : 'grey.900'
            : 'grey.900';

        return (
          <Typography variant="h6" color={daysUntilDueColor}>
            {actionDueDate.toReadable()}
          </Typography>
        );
      },
    },
    {
      label: 'Net call / (distribution)',
      width: 190,
      key: 'netCallDistribution',
      render: ({ netCallDistribution }) =>
        numberToCurrencyString(netCallDistribution, {
          currency: baseCurrency,
        }),
    },
    {
      label: 'Capital call',
      width: 190,
      key: 'capitalCall',
      render: ({ capitalCallTotal }) =>
        numberToCurrencyString(capitalCallTotal, {
          currency: baseCurrency,
        }),
    },
    {
      label: 'Distribution',
      width: 190,
      key: 'distribution',
      render: ({ distributionTotal }) =>
        numberToCurrencyString(distributionTotal, {
          currency: baseCurrency,
        }),
    },
    ...(showBankRecDataInCapitalNotices
      ? [
          {
            label: 'Investors paid',
            key: 'investorsPaid',
            width: 190,
            render: ({
              status,
              isNetDistribution,
              investorsPaid,
              totalInvestors,
            }) => {
              if (status === ActionStatus.Draft || isNetDistribution) {
                return 'N/A';
              }
              return `${investorsPaid}/${totalInvestors}`;
            },
          },
          {
            label: 'Amount paid',
            width: 190,
            key: 'amountPaid',
            render: ({ status, isNetDistribution, amountPaid }) => {
              if (status === ActionStatus.Draft || isNetDistribution) {
                return 'N/A';
              }
              return numberToCurrencyString(amountPaid, {
                currency: baseCurrency,
              });
            },
          },
        ]
      : []),
  ];

  const handleRowExpansion = (row: { id: string }) => {
    if (showBankRecDataInCapitalNotices) {
      toggleRowExpansion(row.id);
    }
  };

  return (
    <Column spacing="lg">
      <PageFilters justify="space-between">
        <Filter size="md">
          <SearchField
            clearable
            fullWidth
            placeholder="Search by action name"
            value={filters.investorName}
            autoSearchOnDebounce
            allowEmptyQuery
            onSearch={(query) => {
              handleChangeFilters({ investorName: query });
            }}
          />
        </Filter>
        <DateRangeFilter>
          <DateRangePicker
            dateRange={filters.actionDueDateRange}
            onDateRangeChange={(dateRange) => {
              handleChangeFilters({ actionDueDateRange: dateRange });
            }}
            placeholder="Select date"
          />
        </DateRangeFilter>
      </PageFilters>
      <Table
        scrollable
        isLoading={false}
        columns={columns}
        rows={filteredCapitalActions}
        pagination={pagination}
        disableSorting
        actions={[
          {
            label: 'Edit capital action',
            icon: Edit,
            disabled: ({ canBeUpdated }) => !canBeUpdated,
            tooltip: ({ reasonCannotBeUpdated }) => reasonCannotBeUpdated,
            hidden: ({ status, capitalNotice }) =>
              status === ActionStatus.Approved || !!capitalNotice?.id,
            onClick: ({ id }) => {
              history.push(createLpCapitalActionEditRoute(id));
            },
          },
          {
            label: ({ capitalNotice }) =>
              !capitalNotice?.id
                ? 'Create capital notices'
                : 'Edit capital notices',
            icon: Add,
            hidden: ({ status }) => status === ActionStatus.Draft,
            onClick: ({ id }) => {
              history.push(createLpCapitalActionNoticeCreateRoute(id));
            },
          },
          {
            label: 'Download capital notices',
            icon: SaveAlt,
            hidden: ({ capitalNotice }) =>
              !capitalNotice?.id ||
              capitalNotice.status !== CapitalActionNoticeStatus.Published,
            onClick: (capitalAction) => setCapitalAction(capitalAction),
          },
          {
            label: 'Recall capital notices',
            icon: Replay,
            hidden: ({ capitalNotice }) =>
              !capitalNotice?.id ||
              capitalNotice.status !== CapitalActionNoticeStatus.Published,
            onClick: ({ capitalNotice }) => {
              if (capitalNotice?.id) {
                recallCapitalNoticesDisclosure.stageAction({
                  id: capitalNotice.id,
                });
              }
            },
          },
          {
            label: 'Delete capital action',
            icon: Delete,
            disabled: ({ canBeDeleted }) => !canBeDeleted,
            tooltip: ({ reasonCannotBeDeleted }) => reasonCannotBeDeleted,
            onClick: ({ id, status }) => {
              if (status === ActionStatus.Approved) {
                setNotifyInvestors(true);
              }
              deleteCapitalActionDisclosure.stageAction({ id, status });
            },
          },
        ]}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        isExpandableRow={() => true}
        getExpandedContent={(capitalAction, tableWidth) => (
          <CapitalActionExpandedContent
            capitalAction={capitalAction}
            tableWidth={tableWidth}
            baseCurrency={baseCurrency}
          />
        )}
        onRowExpand={handleRowExpansion}
        onRowClick={handleRowExpansion}
        emptyMessage="No capital actions found"
      />
      {capitalAction ? (
        <DownloadCapitalNotices
          firmName={capitalAction.firmName}
          capitalActionName={capitalAction.name}
          capitalNotice={capitalAction.capitalNotice}
          investors={capitalAction.perInvestorBreakdown}
          onClose={() => setCapitalAction(null)}
        />
      ) : null}
      <StyledConfirmDialog
        open={recallCapitalNoticesDisclosure.isOpen}
        onClose={recallCapitalNoticesDisclosure.onClose}
        title="Recalling capital notices"
        maxWidth="sm"
        content={
          <Typography variant="body2">
            This cannot be undone. If you need notices in future, new ones must
            be created. An email will be sent to all investors that were
            originally notified by email about this notice, explaining that the
            notice has been recalled.
          </Typography>
        }
        loading={recallCapitalActionNotice.isPending}
        actions={{
          confirm: {
            label: 'Recall notices',
            variant: 'contained',
            color: 'primary',
            onClick: handleRecallCapitalActionNotice,
          },
        }}
      />
      <StyledConfirmDialog
        open={deleteCapitalActionDisclosure.isOpen}
        onClose={deleteCapitalActionDisclosure.onClose}
        title="Deleting capital action"
        maxWidth="sm"
        content={
          deleteCapitalActionDisclosure.actionProps.status ===
          ActionStatus.Approved
            ? notifyInvestors
              ? 'This cannot be undone. Upon deleting this capital action, all investors that received this capital action’s notice will be notified by email (if any), explaining that the notice has been recalled. Any payments subsequently received in relation to the capital action should be refunded (or reconciled to a replacement capital action).'
              : "This cannot be undone. Investors will not be notified about this capital action's deletion. Any payments received in relation to this capital action should be refunded (or reconciled to a replacement capital action)."
            : 'You are about to delete this capital action. This cannot be undone.'
        }
        loading={deleteCapitalAction.isPending}
        actions={{
          confirm: {
            label: 'Delete capital action',
            variant: 'contained',
            color: 'primary',
            onClick: handleDeleteCapitalAction,
          },
          ...(deleteCapitalActionDisclosure.actionProps.status ===
          ActionStatus.Approved
            ? {
                toggle: {
                  checked: notifyInvestors,
                  onChange: setNotifyInvestors,
                  label: 'Notify investors',
                },
              }
            : {}),
        }}
      />
    </Column>
  );
};

export default CapitalActions;
